import type { DppCompletenessCheck } from './dppCompleteness';
import type {
  CategoryCode,
  ComplianceDocumentType,
  MachineClassification,
  Material,
} from './productFormTypes';

export type ComplianceDocumentRequirementKey =
  | ComplianceDocumentType
  | 'DOC_OR_DOI';

export type ComplianceDocumentRequirement = {
  key: ComplianceDocumentRequirementKey;
  label: string;
  description?: string;
  badgeLabel: 'Required' | 'Optional' | 'Conditional';
  isRequired: boolean;
  helperText?: string;
  completenessKey: string;
  completenessLabel: string;
  completenessGap: string;
};

export const complianceDocumentLabels: Record<ComplianceDocumentType, string> =
  {
    DOC: 'EU Declaration of Conformity (DoC)',
    DOI: 'Declaration of Incorporation (DoI)',
    DOP: 'Declaration of Performance (DoP)',
    USER_MANUAL: 'User Manual & Operating Instructions',
    REPAIR_MANUAL: 'Technical Repair & Disassembly Manual',
    EPREL_FICHE: 'EPREL Energy Label & Product Fiche',
    CARBON_FOOTPRINT_REPORT: 'Carbon Footprint Verification Report',
    SAFETY_MANUAL: 'Battery Safety & Handling Manual',
    DISASSEMBLY_GUIDE: 'Assembly & Disassembly Guide',
    MAINTENANCE_GUIDE: 'Maintenance & Repair Instructions',
    DEMOLITION_GUIDE: 'Demolition & Material Separation Guide',
    VOC_CERTIFICATE: 'VOC Emission Test Certificate',
    EUDR_DDS_STATEMENT: 'EUDR Deforestation Due Diligence Statement',
    CARE_INSTRUCTIONS: 'Care & Cleaning Instructions',
    FIBER_TEST_REPORT: 'Fiber Composition Test Report',
    MILL_TEST_CERTIFICATE: 'Mill Test Certificate (EN 10204)',
    CBAM_REPORT: 'CBAM Embedded Carbon Report',
    TYPE_APPROVAL: 'EU Type Approval Certificate',
    TIRE_LABEL_FICHE: 'EU Tire Label Fiche',
    SAFETY_DATA_SHEET: 'Safety Data Sheet (SDS)',
    UFI_DOSSIER: 'Poison Center Notification / UFI Dossier',
    TECHNICAL_DATA_SHEET: 'Technical Data Sheet (TDS)',
  };

export const complianceDocumentDescriptions: Record<
  ComplianceDocumentType,
  string
> = {
  DOC: 'Official signed declaration confirming the product meets all applicable EU health, safety, and environmental protection directives.',
  DOI: 'Required specifically for Partly Completed Machinery (PCM) that cannot function independently prior to final assembly.',
  DOP: 'Attests to essential performance characteristics (e.g., reaction to fire, mechanical resistance) under the Construction Products Regulation.',
  USER_MANUAL:
    'Provides end-users with essential setup instructions, operating procedures, safety warnings, and basic care guidelines.',
  REPAIR_MANUAL:
    'Required for Ecodesign-regulated electronics (e.g., smartphones, tablets, displays). Includes component schematics, teardown steps, and torque specs for professional repairers.',
  EPREL_FICHE:
    'Required for energy-labeled categories (e.g., displays, TVs, smartphones, tablets). Links to your registered record in the official EU EPREL database.',
  CARBON_FOOTPRINT_REPORT:
    'Required for Electric Vehicle (EV) and industrial batteries (>2 kWh). Third-party verified report declaring lifecycle carbon intensity (kg CO2e/kWh).',
  SAFETY_MANUAL:
    'Details UN 38.3 transport safety classifications, thermal runaway prevention, storage limits, and emergency response protocols.',
  DISASSEMBLY_GUIDE:
    'Required for furniture, mattresses, and mechanical equipment to guide recyclers in separating different materials (e.g., foam, metal, springs) at end-of-life.',
  MAINTENANCE_GUIDE:
    'Specifies routine servicing intervals, wear-and-tear inspection schedules, lubrication, and replacement parts for extended product lifespan.',
  DEMOLITION_GUIDE:
    'Provides protocols for demolition contractors and recyclers to safely extract and sort reusable structural building materials.',
  VOC_CERTIFICATE:
    'Required for indoor products (e.g., flooring, paints, adhesives, paneling). Certifies indoor air quality compliance by quantifying volatile organic emissions.',
  EUDR_DDS_STATEMENT:
    'Required if the product contains timber, wood, rubber, or natural leather. Verifies raw materials originate from deforestation-free supply chains.',
  CARE_INSTRUCTIONS:
    'Details washing temperatures, drying limits, and chemical restrictions to prevent degradation and extend fabric/surface life.',
  FIBER_TEST_REPORT:
    'Laboratory test report verifying the exact percentage breakdown of natural and synthetic fibers present in the product.',
  MILL_TEST_CERTIFICATE:
    'Official quality tracking document detailing exact chemical composition, yield strength, and heat batch history for raw metal stock.',
  CBAM_REPORT:
    'Required for imported iron, steel, or aluminum. Declares direct and indirect embedded carbon emissions under EU Carbon Border tariffs.',
  TYPE_APPROVAL:
    'Regulatory certificate confirming that automotive components or systems comply with UN/ECE safety and environmental specifications.',
  TIRE_LABEL_FICHE:
    'Required for passenger (C1), commercial (C2), and heavy-duty (C3) tires. Declares fuel efficiency, wet grip, and noise rating classes.',
  SAFETY_DATA_SHEET:
    'Required for chemical substances and hazardous mixtures. 16-section document detailing chemical properties, health hazards, and spill handling under REACH Article 31.',
  UFI_DOSSIER:
    'Required for hazardous chemical mixtures. Contains the Unique Formula Identifier (UFI) registered with Poison Centers for medical emergency response.',
  TECHNICAL_DATA_SHEET:
    'Summarizes physical performance properties, tolerances, dimensional specs, and material grades for technical evaluation.',
};

export type ComplianceDocumentAccessTier =
  | 'OPEN'
  | 'RESTRICTED'
  | 'CONFIDENTIAL';

export const complianceDocumentAccessTiers: Record<
  ComplianceDocumentType,
  ComplianceDocumentAccessTier
> = {
  DOC: 'OPEN',
  DOI: 'OPEN',
  DOP: 'OPEN',
  USER_MANUAL: 'OPEN',
  REPAIR_MANUAL: 'RESTRICTED',
  EPREL_FICHE: 'OPEN',
  CARBON_FOOTPRINT_REPORT: 'CONFIDENTIAL',
  SAFETY_MANUAL: 'RESTRICTED',
  DISASSEMBLY_GUIDE: 'RESTRICTED',
  MAINTENANCE_GUIDE: 'RESTRICTED',
  DEMOLITION_GUIDE: 'RESTRICTED',
  VOC_CERTIFICATE: 'OPEN',
  EUDR_DDS_STATEMENT: 'CONFIDENTIAL',
  CARE_INSTRUCTIONS: 'OPEN',
  FIBER_TEST_REPORT: 'OPEN',
  MILL_TEST_CERTIFICATE: 'CONFIDENTIAL',
  CBAM_REPORT: 'CONFIDENTIAL',
  TYPE_APPROVAL: 'OPEN',
  TIRE_LABEL_FICHE: 'OPEN',
  SAFETY_DATA_SHEET: 'RESTRICTED',
  UFI_DOSSIER: 'CONFIDENTIAL',
  TECHNICAL_DATA_SHEET: 'OPEN',
};

const accessTierRank: Record<ComplianceDocumentAccessTier, number> = {
  OPEN: 0,
  RESTRICTED: 1,
  CONFIDENTIAL: 2,
};

export const getComplianceDocumentAccessTier = (
  documentType: ComplianceDocumentType,
): ComplianceDocumentAccessTier =>
  complianceDocumentAccessTiers[documentType] ?? 'RESTRICTED';

export const canAccessComplianceDocumentTier = (
  grantedTier: ComplianceDocumentAccessTier,
  requiredTier: ComplianceDocumentAccessTier,
) => accessTierRank[grantedTier] >= accessTierRank[requiredTier];

export const getLegacyRepairManualDocumentType = (
  categoryCode: CategoryCode | string | '',
): ComplianceDocumentType => {
  switch (categoryCode) {
    case 'ELECTRONICS_ICT':
      return 'USER_MANUAL';
    case 'BATTERIES_STORAGE':
      return 'SAFETY_MANUAL';
    case 'INDUSTRIAL_MACHINERY':
      return 'MAINTENANCE_GUIDE';
    default:
      return 'MAINTENANCE_GUIDE';
  }
};

const containsWoodOrLeatherMaterials = (materials: Material[]) =>
  materials.some((material) =>
    /\b(wood|timber|oak|pine|beech|birch|plywood|veneer|leather|hide|suede)\b/i.test(
      material.material_name,
    ),
  );

const isPartlyCompletedMachinery = (
  machineClassification: MachineClassification | '',
) =>
  machineClassification === 'PARTLY_COMPLETED' ||
  machineClassification === 'PARTLY_COMPLETED_MACHINERY';

const createRequirement = (
  key: ComplianceDocumentRequirementKey,
  options: {
    label: string;
    description?: string;
    badgeLabel: 'Required' | 'Optional' | 'Conditional';
    isRequired: boolean;
    helperText?: string;
    completenessKey: string;
    completenessLabel: string;
    completenessGap: string;
  },
): ComplianceDocumentRequirement => ({
  key,
  label: options.label,
  description: options.description,
  badgeLabel: options.badgeLabel,
  isRequired: options.isRequired,
  helperText: options.helperText,
  completenessKey: options.completenessKey,
  completenessLabel: options.completenessLabel,
  completenessGap: options.completenessGap,
});

export const normalizeComplianceDocumentPaths = (
  value: unknown,
): Partial<Record<ComplianceDocumentType, string>> => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const entries = Object.entries(value as Record<string, unknown>);

  return entries.reduce<Partial<Record<ComplianceDocumentType, string>>>(
    (accumulator, [key, pathValue]) => {
      if (
        typeof pathValue === 'string' &&
        pathValue.trim() &&
        key in complianceDocumentLabels
      ) {
        accumulator[key as ComplianceDocumentType] = pathValue;
      }

      return accumulator;
    },
    {},
  );
};

export const getCategoryComplianceDocumentRequirements = ({
  categoryCode,
  machineClassification,
  materials = [],
}: {
  categoryCode: CategoryCode | '';
  machineClassification?: MachineClassification | '';
  materials?: Material[];
}): ComplianceDocumentRequirement[] => {
  const woodOrLeatherRequired = containsWoodOrLeatherMaterials(materials);

  switch (categoryCode) {
    case 'ELECTRONICS_ICT':
      return [
        createRequirement('DOC', {
          label: complianceDocumentLabels.DOC,
          description: complianceDocumentDescriptions.DOC,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'electronics-doc',
          completenessLabel:
            'Electronics declaration of conformity is attached',
          completenessGap: 'Attach the EU Declaration of Conformity (DoC).',
        }),
        createRequirement('USER_MANUAL', {
          label: complianceDocumentLabels.USER_MANUAL,
          description: complianceDocumentDescriptions.USER_MANUAL,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'electronics-user-manual',
          completenessLabel: 'Electronics user manual is attached',
          completenessGap: 'Attach the user manual and operating instructions.',
        }),
        createRequirement('REPAIR_MANUAL', {
          label: complianceDocumentLabels.REPAIR_MANUAL,
          description: complianceDocumentDescriptions.REPAIR_MANUAL,
          badgeLabel: 'Optional',
          isRequired: false,
          completenessKey: 'electronics-repair-manual',
          completenessLabel: 'Electronics repair manual is attached',
          completenessGap:
            'Attach the technical repair and disassembly manual when available.',
        }),
        createRequirement('EPREL_FICHE', {
          label: complianceDocumentLabels.EPREL_FICHE,
          description: complianceDocumentDescriptions.EPREL_FICHE,
          badgeLabel: 'Optional',
          isRequired: false,
          completenessKey: 'electronics-eprel',
          completenessLabel: 'Electronics EPREL fiche is attached',
          completenessGap:
            'Attach the EPREL energy label and product fiche when available.',
        }),
      ];
    case 'BATTERIES_STORAGE':
      return [
        createRequirement('DOC', {
          label: complianceDocumentLabels.DOC,
          description: complianceDocumentDescriptions.DOC,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'battery-doc',
          completenessLabel: 'Battery declaration of conformity is attached',
          completenessGap: 'Attach the EU Declaration of Conformity (DoC).',
        }),
        createRequirement('SAFETY_MANUAL', {
          label: complianceDocumentLabels.SAFETY_MANUAL,
          description: complianceDocumentDescriptions.SAFETY_MANUAL,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'battery-safety-manual',
          completenessLabel: 'Battery safety manual is attached',
          completenessGap: 'Attach the battery safety and handling manual.',
        }),
        createRequirement('CARBON_FOOTPRINT_REPORT', {
          label: complianceDocumentLabels.CARBON_FOOTPRINT_REPORT,
          description: complianceDocumentDescriptions.CARBON_FOOTPRINT_REPORT,
          badgeLabel: 'Optional',
          isRequired: false,
          completenessKey: 'battery-carbon-report',
          completenessLabel: 'Battery carbon footprint report is attached',
          completenessGap: 'Attach the carbon footprint verification report.',
        }),
      ];
    case 'INDUSTRIAL_MACHINERY':
      return [
        createRequirement('DOC_OR_DOI', {
          label: isPartlyCompletedMachinery(machineClassification ?? '')
            ? complianceDocumentLabels.DOI
            : machineClassification
              ? complianceDocumentLabels.DOC
              : `${complianceDocumentLabels.DOC} or ${complianceDocumentLabels.DOI}`,
          description: isPartlyCompletedMachinery(machineClassification ?? '')
            ? complianceDocumentDescriptions.DOI
            : machineClassification
              ? complianceDocumentDescriptions.DOC
              : `${complianceDocumentDescriptions.DOC} ${complianceDocumentDescriptions.DOI}`,
          badgeLabel: 'Required',
          isRequired: true,
          helperText: isPartlyCompletedMachinery(machineClassification ?? '')
            ? 'Partly completed machinery requires a Declaration of Incorporation.'
            : 'Upload the signed machinery declaration as a PDF.',
          completenessKey: 'industrial-machinery-declaration',
          completenessLabel: 'Industrial machinery declaration is attached',
          completenessGap:
            'Attach the machinery declaration required for this CE classification.',
        }),
        createRequirement('DISASSEMBLY_GUIDE', {
          label: complianceDocumentLabels.DISASSEMBLY_GUIDE,
          description: complianceDocumentDescriptions.DISASSEMBLY_GUIDE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'industrial-disassembly-guide',
          completenessLabel: 'Industrial disassembly guide is attached',
          completenessGap: 'Attach the assembly and disassembly guide.',
        }),
        createRequirement('MAINTENANCE_GUIDE', {
          label: complianceDocumentLabels.MAINTENANCE_GUIDE,
          description: complianceDocumentDescriptions.MAINTENANCE_GUIDE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'industrial-maintenance-guide',
          completenessLabel: 'Industrial maintenance guide is attached',
          completenessGap: 'Attach the maintenance and repair instructions.',
        }),
      ];
    case 'CONSTRUCTION_PRODUCTS':
      return [
        createRequirement('DOP', {
          label: complianceDocumentLabels.DOP,
          description: complianceDocumentDescriptions.DOP,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'construction-dop',
          completenessLabel: 'Construction DoP is attached',
          completenessGap: 'Attach the Declaration of Performance (DoP).',
        }),
        createRequirement('DEMOLITION_GUIDE', {
          label: complianceDocumentLabels.DEMOLITION_GUIDE,
          description: complianceDocumentDescriptions.DEMOLITION_GUIDE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'construction-demolition-guide',
          completenessLabel: 'Construction demolition guide is attached',
          completenessGap:
            'Attach the demolition and material separation guide.',
        }),
      ];
    case 'FURNITURE_MATTRESSES':
      return [
        createRequirement('DISASSEMBLY_GUIDE', {
          label: complianceDocumentLabels.DISASSEMBLY_GUIDE,
          description: complianceDocumentDescriptions.DISASSEMBLY_GUIDE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'furniture-disassembly-guide',
          completenessLabel: 'Furniture disassembly guide is attached',
          completenessGap: 'Attach the assembly and disassembly guide.',
        }),
        createRequirement('EUDR_DDS_STATEMENT', {
          label: complianceDocumentLabels.EUDR_DDS_STATEMENT,
          description: complianceDocumentDescriptions.EUDR_DDS_STATEMENT,
          badgeLabel: woodOrLeatherRequired ? 'Required' : 'Conditional',
          isRequired: woodOrLeatherRequired,
          helperText:
            'Required when the BOM includes wood or leather materials.',
          completenessKey: 'furniture-eudr-dds-statement',
          completenessLabel: 'Furniture EUDR DDS statement is attached',
          completenessGap:
            'Attach the EUDR due diligence statement when wood or leather is present.',
        }),
      ];
    case 'TEXTILES_APPAREL':
      return [
        createRequirement('CARE_INSTRUCTIONS', {
          label: complianceDocumentLabels.CARE_INSTRUCTIONS,
          description: complianceDocumentDescriptions.CARE_INSTRUCTIONS,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'textile-care-instructions',
          completenessLabel: 'Textile care instructions are attached',
          completenessGap: 'Attach the care and cleaning instructions.',
        }),
        createRequirement('FIBER_TEST_REPORT', {
          label: complianceDocumentLabels.FIBER_TEST_REPORT,
          description: complianceDocumentDescriptions.FIBER_TEST_REPORT,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'textile-fiber-test-report',
          completenessLabel: 'Textile fiber test report is attached',
          completenessGap: 'Attach the fiber composition test report.',
        }),
      ];
    case 'IRON_STEEL_METALS':
      return [
        createRequirement('MILL_TEST_CERTIFICATE', {
          label: complianceDocumentLabels.MILL_TEST_CERTIFICATE,
          description: complianceDocumentDescriptions.MILL_TEST_CERTIFICATE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'metals-mill-test-certificate',
          completenessLabel: 'Mill test certificate is attached',
          completenessGap: 'Attach the mill test certificate.',
        }),
        createRequirement('CBAM_REPORT', {
          label: complianceDocumentLabels.CBAM_REPORT,
          description: complianceDocumentDescriptions.CBAM_REPORT,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'metals-cbam-report',
          completenessLabel: 'CBAM report is attached',
          completenessGap: 'Attach the CBAM embedded carbon report.',
        }),
      ];
    case 'TIRES_AUTOMOTIVE':
      return [
        createRequirement('TYPE_APPROVAL', {
          label: complianceDocumentLabels.TYPE_APPROVAL,
          description: complianceDocumentDescriptions.TYPE_APPROVAL,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'tires-type-approval',
          completenessLabel: 'Type approval certificate is attached',
          completenessGap: 'Attach the EU type approval certificate.',
        }),
        createRequirement('TIRE_LABEL_FICHE', {
          label: complianceDocumentLabels.TIRE_LABEL_FICHE,
          description: complianceDocumentDescriptions.TIRE_LABEL_FICHE,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'tires-label-fiche',
          completenessLabel: 'Tire label fiche is attached',
          completenessGap: 'Attach the EU tire label fiche.',
        }),
      ];
    case 'CHEMICALS':
      return [
        createRequirement('SAFETY_DATA_SHEET', {
          label: complianceDocumentLabels.SAFETY_DATA_SHEET,
          description: complianceDocumentDescriptions.SAFETY_DATA_SHEET,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'chemicals-sds',
          completenessLabel: 'Safety data sheet is attached',
          completenessGap: 'Attach the safety data sheet (SDS).',
        }),
        createRequirement('UFI_DOSSIER', {
          label: complianceDocumentLabels.UFI_DOSSIER,
          description: complianceDocumentDescriptions.UFI_DOSSIER,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'chemicals-ufi-dossier',
          completenessLabel: 'UFI dossier is attached',
          completenessGap:
            'Attach the poison center notification or UFI dossier.',
        }),
      ];
    case 'OTHER_HARDWARE':
      return [
        createRequirement('TECHNICAL_DATA_SHEET', {
          label: complianceDocumentLabels.TECHNICAL_DATA_SHEET,
          description: complianceDocumentDescriptions.TECHNICAL_DATA_SHEET,
          badgeLabel: 'Required',
          isRequired: true,
          completenessKey: 'hardware-tds',
          completenessLabel: 'Technical data sheet is attached',
          completenessGap: 'Attach the technical data sheet.',
        }),
      ];
    default:
      return [];
  }
};

export const buildComplianceDocumentCompletenessChecks = (
  requirements: ComplianceDocumentRequirement[],
  hasDocument: (requirement: ComplianceDocumentRequirement) => boolean,
): DppCompletenessCheck[] =>
  requirements
    .filter((requirement) => requirement.isRequired)
    .map((requirement) => ({
      key: requirement.completenessKey,
      label: requirement.completenessLabel,
      complete: hasDocument(requirement),
      gap: requirement.completenessGap,
    }));
